/**
 * Detects if the current device has a touch screen
 * Uses multiple methods for more accurate detection
 * Special handling for Cast devices
 */
export function detectTouchDevice(): boolean {
  console.log('📱 Running touch device detection');
  
  // First, check if we're in a Cast environment and can get info directly
  try {
    // Check if we're on a Cast device with a deviceCapabilities property
    if (window.cast?.framework?.system?.SystemContext) {
      // Try to get the device capabilities from Cast SDK
      const context = window.cast.framework.CastReceiverContext.getInstance();
      const options = window.cast.framework.CastReceiverOptions ? new window.cast.framework.CastReceiverOptions() : null;
      
      // Access deviceCapabilities if available (from the ready event)
      const deviceCaps = (context as any)?.deviceCapabilities;
      
      if (deviceCaps?.touch_input_supported === true) {
        console.log('✅ Device EXPLICITLY reports touch support via Cast SDK');
        return true;
      }
      
      // Check for receiver options touch controls flag (if available)
      if (options && (options as any)?.touchScreenOptimizedApp) {
        console.log('✅ Cast receiver is configured as touchScreenOptimizedApp');
        return true;
      }
    }
  } catch (e) {
    console.log('Error checking Cast device capabilities:', e);
  }
  
  // Standard detection methods
  // Check if the device has touch points
  const hasTouchPoints = navigator.maxTouchPoints > 0;
  console.log(`- Touch points: ${navigator.maxTouchPoints} (${hasTouchPoints ? 'YES' : 'NO'})`);
  
  // Check for touch event support
  const hasTouchEvents = 'ontouchstart' in window;
  console.log(`- Touch events supported: ${hasTouchEvents ? 'YES' : 'NO'}`);
  
  // Check for specific touch API
  const hasPointerEvents = window.matchMedia('(pointer: coarse)').matches;
  console.log(`- Coarse pointer: ${hasPointerEvents ? 'YES' : 'NO'}`);
  
  // Check user agent for mobile/tablet devices
  const hasMobileUA = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  console.log(`- Mobile user agent: ${hasMobileUA ? 'YES' : 'NO'}`);
  
  // Check for Chrome Cast device that specifically mentions touch
  const isCastTouchDevice = /CrKey/i.test(navigator.userAgent) && 
                            /(touch|tablet)/i.test(navigator.userAgent);
  console.log(`- Cast touch device: ${isCastTouchDevice ? 'YES' : 'NO'}`);
  
  // A device is considered touch-enabled if it passes at least two tests
  const touchTests = [hasTouchPoints, hasTouchEvents, hasPointerEvents, hasMobileUA, isCastTouchDevice];
  const passedTests = touchTests.filter(Boolean).length;
  
  const result = passedTests >= 2 || isCastTouchDevice;
  console.log(`📱 Touch detection result: ${result ? 'TOUCH SUPPORTED' : 'NO TOUCH SUPPORT'} (${passedTests} tests passed)`);
  
  // Check for Cast environment explicitly and enable touch
  if (/CrKey/i.test(navigator.userAgent)) {
    console.log('🚧 CAST DEVICE DETECTED: Enabling touch support by default');
    return true;
  }
  
  // Always enable touch for easier testing when in development environment
  if (process.env.NODE_ENV === 'development') {
    const forceTouchInDev = true; // Set to true to force touch mode in development
    if (forceTouchInDev) {
      console.log('🚧 DEVELOPMENT MODE: Forcing touch support for testing!');
      return true;
    }
  }

  // TEMPORARY FIX: Force touch mode for all environments
  console.log('🚧 FORCING TOUCH SUPPORT regardless of environment');
  return true;
  
  // Check localStorage for developer override
  if (localStorage.getItem('devTouchMode') === 'true') {
    console.log('🚧 DEV TOUCH MODE: Manually enabled from localStorage');
    return true;
  }
  
  return result;
}

/**
 * Returns information about the current device
 */
export function getDeviceInfo() {
  return {
    screenWidth: window.innerWidth,
    screenHeight: window.innerHeight,
    userAgent: navigator.userAgent,
    hasTouchScreen: detectTouchDevice(),
    isTV: /\bTV\b|\bAndroidTV\b|\bGoogleTV\b|\bCrKey\b/.test(navigator.userAgent),
  }
}

export default { detectTouchDevice, getDeviceInfo }
